import React from "react";
import {
  BlockOutlined,
  CheckCircleOutlined,
  CheckBoxOutlined,
  EventOutlined,
  HighlightOff,
  HourglassEmptyOutlined,
  NotInterestedOutlined,
  RemoveCircleOutline,
  ReportOutlined,
  WarningOutlined,
} from "@material-ui/icons";
import { StatusType } from "./statusTypes";

export type StatusIconComponent = React.ComponentType<{
  fontSize?: "inherit" | "default" | "small" | "large";
  className?: string;
}>;

/**
 * Icon mapping per Icon Specifications for Test Status and Result (master matrix).
 * Icon color is applied at render time via getStatusMainColor() / CSS variables
 * (--status-*) so light/dark hex codes swap automatically with data-theme.
 *
 * | Status            | Spec icon              | Component              |
 * |-------------------|------------------------|------------------------|
 * | Pass              | Check-Circle           | CheckCircleOutlined    |
 * | Fail              | X-Circle               | HighlightOff           |
 * | Failed & Resolved | Check-Square / Wrench  | CheckBoxOutlined       |
 * | Due               | Calendar / Bell        | EventOutlined          |
 * | Overdue           | Alert octagon          | ReportOutlined         |
 * | Missed            | Alert-Triangle         | WarningOutlined        |
 * | Incomplete        | Minus-Circle           | RemoveCircleOutline    |
 * | Pending           | Hourglass / Clock      | HourglassEmptyOutlined |
 * | Skipped           | Circle-Slash           | NotInterestedOutlined  |
 * | Excluded          | Circle-Slash           | NotInterestedOutlined  |
 */
const STATUS_ICON_MAP: Record<StatusType, StatusIconComponent> = {
  pass: CheckCircleOutlined,
  fail: HighlightOff,
  failed_resolved: CheckBoxOutlined,
  due: EventOutlined,
  overdue: ReportOutlined,
  missed: WarningOutlined,
  incomplete: RemoveCircleOutline,
  pending: HourglassEmptyOutlined,
  skipped: NotInterestedOutlined,
  not_due: HourglassEmptyOutlined,
  complete: CheckCircleOutlined,
  excluded: NotInterestedOutlined,
};

export function getStatusIconComponent(status: StatusType): StatusIconComponent {
  return STATUS_ICON_MAP[status] ?? BlockOutlined;
}

export function renderStatusIcon(
  status: StatusType,
  props?: { fontSize?: "inherit" | "default" | "small" | "large"; className?: string }
): JSX.Element {
  const Icon = getStatusIconComponent(status);
  return <Icon {...props} />;
}

/** Skipped and excluded results both use Circle-Slash. */
export function getExcludedIconComponent(): StatusIconComponent {
  return NotInterestedOutlined;
}
